import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DateTime } from 'luxon';
import { apiGateway } from '../../../../config';

import type { AxiosResponse } from 'axios';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../../redux/store';
import type { ErrorResponse } from '../../../../@types/common';
import type { Port } from '../../../../@types/common';
import type { ShippingScheduleHistory, ShippingScheduleHistoryState } from '../../../../@types/shippingSchedule';

// ----------------------------------------------------------------------

const initialState: ShippingScheduleHistoryState = {
  isLoading: false,
  hasError: false,
  hasSearched: false,
  message: undefined,
  options: {
    port: undefined,
  },
  shippingSchedules: undefined,
  dateFrom: DateTime.now().startOf('month').minus({months: 2}).toISO(),
  dateTo: DateTime.now().endOf('month').toISO(),
};

const shippingScheduleSlice = createSlice({
  name: 'generalShippingScheduleHistory',
  initialState,
  reducers: {

    startLoading(state: ShippingScheduleHistoryState) {
      state.isLoading = true;
    },

    hasError(state: ShippingScheduleHistoryState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = true;
      state.message = action.payload;
    },

    resetMessage(state: ShippingScheduleHistoryState) {
      state.isLoading = false;
      state.hasError = false;
      state.message = '';
    },

    // ----------------------------------------------------------------------
    
    hasSearched(state: ShippingScheduleHistoryState, action: PayloadAction<boolean>) {
      state.hasSearched = action.payload;
    },

    changeStartDate(state: ShippingScheduleHistoryState, action: PayloadAction<string | undefined>) {
      state.dateFrom = action.payload;
    },

    changeEndDate(state: ShippingScheduleHistoryState, action: PayloadAction<string | undefined>) {
      state.dateTo = action.payload;
    },

    // ----------------------------------------------------------------------

    getOptionsPortSuccess(state: ShippingScheduleHistoryState, action: PayloadAction<Port[]>) {
      state.isLoading = false;
      state.options.port = action.payload;
    },

    // ----------------------------------------------------------------------

    getShippingSchedulesSuccess(state: ShippingScheduleHistoryState, action: PayloadAction<ShippingScheduleHistory[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.shippingSchedules = action.payload;
    },

  },
});

export default shippingScheduleSlice.reducer;


// ----------------------------------------------------------------------

export function resetMessage(): AppThunk {
  return (dispatch): void => {
    dispatch(shippingScheduleSlice.actions.resetMessage());
  }
};

export function changeStartDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(shippingScheduleSlice.actions.changeStartDate(date));
  }
};

export function changeEndDate(date: string | undefined): AppThunk {
  return (dispatch): void => {
    dispatch(shippingScheduleSlice.actions.changeEndDate(date));
  }
};

// ----------------------------------------------------------------------

export function getOptionsPort(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(shippingScheduleSlice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<Port[]>(
        apiGateway + '/options/port',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          timeout: 60000
        }
      );
      dispatch(shippingScheduleSlice.actions.getOptionsPortSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(shippingScheduleSlice.actions.hasError(serverResponse.message));
        } else {
          dispatch(shippingScheduleSlice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(shippingScheduleSlice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(shippingScheduleSlice.actions.hasError(message));
        }
      }
    }
  };
};

// ----------------------------------------------------------------------
// ShippingScheduleId単位でのオーダー検索
export function searchShippingSchedule(id: string, dateFrom: string | undefined, dateTo: string | undefined): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(shippingScheduleSlice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<ShippingScheduleHistory[]>(
        apiGateway + '/shipHistory/search/' + id,
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            dateFrom,
            dateTo
          },
          timeout: 60000
        }
      );
      dispatch(shippingScheduleSlice.actions.hasSearched(true));
      dispatch(shippingScheduleSlice.actions.getShippingSchedulesSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(shippingScheduleSlice.actions.hasError(serverResponse.message));
        } else {
          dispatch(shippingScheduleSlice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(shippingScheduleSlice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(shippingScheduleSlice.actions.hasError(message));
        }
      }
    }
  };
};

// 通常のオーダー検索
export function searchShippingSchedules(dateFrom: string | undefined, dateTo: string | undefined): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(shippingScheduleSlice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<ShippingScheduleHistory[]>(
        apiGateway + '/shipHistory/search',
        {
          headers: {
            'Authorization': 'Bearer ' + token,
          },
          params: {
            dateFrom,
            dateTo
          },
          timeout: 60000
        }
      );
      dispatch(shippingScheduleSlice.actions.hasSearched(true));
      dispatch(shippingScheduleSlice.actions.getShippingSchedulesSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(shippingScheduleSlice.actions.hasError(serverResponse.message));
        } else {
          dispatch(shippingScheduleSlice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(shippingScheduleSlice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(shippingScheduleSlice.actions.hasError(message));
        }
      }
    }
  };
};
